import { useEffect, useState } from 'react';
import clsx from 'clsx';

import { isProd } from '../../utilities';
import { Loading } from '../../components';

type ConversationProps = {
  agentId: string;
  children: React.ReactNode;
};

const overrides = {
  isOpened: true,
  startWithStaticWidget: false,
  widgetTranscribeMode: 'extended',
};

const blocksClassName =
  'shrink-0 transition-default duration-500 basis-1/3 max-w-[65%] grow empty:grow-0 empty:basis-0';

const Conversation: React.FC<ConversationProps> = ({ agentId, children }) => {
  const [loading, setLoading] = useState(true);
  const [chatNode, setChatNode] = useState<HTMLElement | null>();
  const [popupNode, setPopupNode] = useState<HTMLElement | null>();

  useEffect(() => {
    if (!agentId || !chatNode || !popupNode) return;

    const API = window.AiAgentsWebWidget;
    if (!API) return;

    const init = async () => {
      API.before?.({ cf: overrides });
      await API.init({
        ...overrides,

        authToken: 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9VyX2lkIjoxLC',
        authUrl: isProd
          ? 'https://uat-api.nextlevel.ai/livekit/v1/live-kit/get-token'
          : 'https://dev-api.nextlevel.ai/livekit/v1/live-kit/get-token',
        agentId,

        container: chatNode,
        enableEmbeddedTranscribe: true,
        pluginsParams: { 'html-popup': { container: popupNode } },

        transcribeWidgetInputElementType: 'textarea',
        transcribeWidgetInputParams: { rows: 2 },
      });

      setLoading(false);
    };

    init();

    return () => {
      API.destroy?.(true);
    };
  }, [agentId, chatNode, popupNode]);

  return (
    <div className="w-full h-full flex items-center justify-center">
      <section
        className={clsx(
          'flex flex-row justify-center',
          loading ? 'hidden' : 'w-full h-full'
        )}
      >
        <div className="overflow-y-hidden h-full grow flex flex-row">
          <div
            className={clsx(
              'ml-auto mr-auto flex flex-col pl-lg pr-sm',
              blocksClassName
            )}
          >
            <div
              key="chat"
              ref={setChatNode}
              className="grow-1 shrink-0 flex-1 overflow-hidden"
            />
            {!loading && children}
          </div>
          <div
            key="popup"
            ref={setPopupNode}
            className={clsx('ml-lg empty:ml-0', blocksClassName)}
          />
        </div>
      </section>
      {loading ? <Loading /> : null}
    </div>
  );
};

export default Conversation;
